/**
 * /operations/payroll-candidates — PayrollRunCandidate queue
 * (M30.5.1).
 *
 * The list view of M30 P8's PayrollRunCandidate table. Every
 * HR-initiated transaction that produces a payroll line item
 * lands here; the next payroll run picks them up.
 *
 * Default filter: status='queued' (the work waiting to be picked
 * up). The status filter pill lets HR see included / failed /
 * cancelled rows too.
 *
 * Bilingual + RTL clean. All identifiers (transaction_type,
 * status, source_reason_code, period YYYY-MM) wrapped in <LtrText>
 * so the AR layout doesn't reorder them. Amounts via <LtrAmount>
 * (M30.5 hard constraint — every money render uses the primitive).
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  EmptyState,
  LtrAmount,
  LtrText,
  PageHeader,
  Skeleton,
} from "../components/ui";
import {
  api,
  type PayrollCandidateRow,
} from "../lib/api";

export function PayrollCandidatesPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";

  const queue = useQuery({
    queryKey: ["payroll-candidates", "queued"],
    queryFn: () => api.listPayrollCandidates({ statusFilter: "queued" }),
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto flex max-w-workspace flex-col gap-6 p-6">
      <PageHeader
        title={t("operations.payroll_candidates_title")}
        subtitle={t("operations.payroll_candidates_subtitle")}
      />

      {queue.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : queue.isError ? (
        <div className="rounded-md border border-state-denied/30 bg-state-denied-bg/40 p-4 text-sm text-state-denied">
          {String(queue.error)}
        </div>
      ) : queue.data!.items.length === 0 ? (
        <EmptyState
          headline={t("operations.payroll_candidates_empty_headline")}
          description={t("operations.payroll_candidates_empty_body")}
        />
      ) : (
        <CandidatesTable items={queue.data!.items} isAr={isAr} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function CandidatesTable({
  items,
  isAr,
}: {
  items: PayrollCandidateRow[];
  isAr: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section className="overflow-hidden rounded-lg border border-border-subtle bg-surface-raised shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-surface-sunken text-xs uppercase text-text-secondary">
          <tr>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.payroll_candidates_col_transaction_type")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.payroll_candidates_col_employee")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.payroll_candidates_col_period")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.payroll_candidates_col_type")}
            </th>
            <th className="px-4 py-2 text-end font-medium">
              {t("operations.payroll_candidates_col_amount")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.payroll_candidates_col_reason")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.payroll_candidates_col_queued_at")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row.id}
              className="border-t border-border-subtle"
            >
              <td className="px-4 py-2 text-text-primary">
                <LtrText>{row.transaction_type}</LtrText>
              </td>
              <td className="px-4 py-2 text-text-secondary">
                <LtrText>{row.employee_id.slice(0, 8)}</LtrText>
              </td>
              <td className="px-4 py-2 text-text-secondary">
                <LtrText>{row.target_period_year_month}</LtrText>
              </td>
              <td className="px-4 py-2 text-text-secondary">
                <LtrText>{row.line_item_type}</LtrText>
              </td>
              <td className="px-4 py-2 text-end font-medium text-text-primary">
                <LtrAmount
                  value={Number(row.amount)}
                  currency={row.currency}
                />
              </td>
              <td className="px-4 py-2 text-text-secondary">
                {row.source_reason_code ? (
                  <LtrText>{row.source_reason_code}</LtrText>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-2 text-text-secondary">
                {formatWhen(row.queued_at, isAr)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function formatWhen(iso: string, isAr: boolean): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(isAr ? "ar-SA" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
